import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cloneDeep, isEqual, set } from 'lodash';
import * as yup from 'yup';

import { FormControlBuffer } from './sources';
import type { DataSource, FormControlConfig, FormControls } from './types';

export type FormControlsWithDataSource<TForm extends object> = FormControls<TForm> & DataSource<TForm>;

export default function useFormControlsFor<TData, TForm extends object, TPayload = unknown>(
  datasource: DataSource<TData>,
  config: FormControlConfig<TData, TForm, TPayload>
): FormControlsWithDataSource<TForm> {
  const sourceData = datasource.value;
  const sourceLoading = datasource.loading;

  // Stable ref for config to avoid dependency churn (config is typically an object literal)
  const configRef = useRef(config);
  configRef.current = config;

  // Derive initial form values from upstream data
  const initialFormValues = useMemo(() => configRef.current.toFormValues(sourceData), [sourceData]);

  // Internal form state
  const [formValues, setFormValues] = useState<TForm>(() => cloneDeep(initialFormValues));
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Snapshot for dirty comparison
  const initialRef = useRef<TForm>(cloneDeep(initialFormValues));

  // Re-sync when upstream data changes (e.g. query refetch after save)
  useEffect(() => {
    if (!isEqual(initialRef.current, initialFormValues)) {
      initialRef.current = cloneDeep(initialFormValues);
      setFormValues(cloneDeep(initialFormValues));
      setFormErrors({});
    }
  }, [initialFormValues]);

  const dirty = useMemo(() => !isEqual(initialRef.current, formValues), [formValues]);

  // --- Validation ---

  const validateField = useCallback(
    async (name: string, values?: TForm): Promise<Record<string, string>> => {
      const vals = values ?? formValues;
      const errors: Record<string, string> = {};
      const { schema } = configRef.current;

      if (schema) {
        try {
          await schema.validateAt(name, vals);
          setFormErrors((prev) => {
            const next = { ...prev };
            delete next[name];
            return next;
          });
        } catch (error) {
          if (yup.ValidationError.isError(error)) {
            const msg = error.message;
            setFormErrors((prev) => ({ ...prev, [name]: msg }));
            errors[name] = msg;
          }
        }
      }

      return errors;
    },
    [formValues]
  );

  const validateForm = useCallback(async (): Promise<Record<string, string>> => {
    const errors: Record<string, string> = {};
    const { schema, validate } = configRef.current;

    if (schema) {
      try {
        await schema.validate(formValues, { abortEarly: false });
      } catch (error) {
        if (yup.ValidationError.isError(error)) {
          error.inner.forEach((e) => {
            if (e.path) set(errors, e.path, e.message);
          });
        }
      }
    }

    if (validate) {
      Object.assign(errors, validate(formValues));
    }

    setFormErrors({ ...errors });
    return errors;
  }, [formValues]);

  // Derived valid state: passes validation AND is dirty
  const [formIsValid, setFormIsValid] = useState(false);

  useEffect(() => {
    const check = async () => {
      if (!dirty) {
        setFormIsValid(false);
        return;
      }

      const errors: Record<string, string> = {};
      const { schema, validate } = configRef.current;

      if (schema) {
        try {
          await schema.validate(formValues, { abortEarly: false });
        } catch (error) {
          if (yup.ValidationError.isError(error)) {
            error.inner.forEach((e) => {
              if (e.path) set(errors, e.path, e.message);
            });
          }
        }
      }

      if (validate) {
        Object.assign(errors, validate(formValues));
      }

      setFormIsValid(Object.keys(errors).length === 0);
    };

    check();
  }, [formValues, dirty]);

  // --- Mutators ---

  const setValue = useCallback(
    (key: string, value: any, options: { validate?: boolean } = { validate: true }) => {
      setFormValues((prev) => {
        const updated = set({ ...prev }, key, value) as TForm;
        if (options.validate) validateField(key, updated);
        return { ...updated };
      });
    },
    [validateField]
  );

  const setValues = useCallback(
    (values: Partial<TForm>, options: { validate?: boolean } = { validate: true }) => {
      setFormValues((prev) => {
        const updated = { ...prev, ...values } as TForm;
        if (options.validate) {
          for (const key in values) {
            validateField(key, updated);
          }
        }
        return { ...updated };
      });
    },
    [validateField]
  );

  // --- Submit / Cancel ---

  const submit = useCallback(async () => {
    if (submitting) return;

    const errors = await validateForm();
    if (Object.keys(errors).length) return;

    setSubmitting(true);
    try {
      const payload = configRef.current.toPayload(formValues);
      await datasource.save(payload);
    } finally {
      setSubmitting(false);
    }
  }, [submitting, validateForm, formValues, datasource]);

  const cancel = useCallback(() => {
    setFormValues(cloneDeep(initialRef.current));
    setFormErrors({});
  }, []);

  // --- FormControlBuffer (DataSource backed by form state) ---

  const bufferRef = useRef<FormControlBuffer<TForm>>(null!);
  if (bufferRef.current === null) {
    bufferRef.current = new FormControlBuffer<TForm>();
  }

  bufferRef.current._syncState({
    formValues,
    loading: sourceLoading,
    onUpdate: (updatedValues: TForm) => {
      setFormValues({ ...updatedValues });
    },
  });

  // Return a merged object that satisfies both FormControls and DataSource
  return useMemo(() => {
    const buffer = bufferRef.current;
    return {
      // FormControls
      values: formValues,
      errors: formErrors,
      valid: formIsValid,
      dirty,
      submitting,
      loading: sourceLoading,
      setValue,
      setValues,
      submit,
      cancel,
      validateField,
      validateForm,
      // DataSource
      get value() {
        return buffer.value;
      },
      save: buffer.save.bind(buffer),
      get: buffer.get.bind(buffer) as DataSource<TForm>['get'],
      findBy: buffer.findBy.bind(buffer) as DataSource<TForm>['findBy'],
    };
  }, [
    formValues,
    formErrors,
    formIsValid,
    dirty,
    submitting,
    sourceLoading,
    setValue,
    setValues,
    submit,
    cancel,
    validateField,
    validateForm,
  ]);
}
